import { ChangeEvent, useRef, useState } from 'react';
import { api, FileEntry, formatDate, SftpInfo } from '../../api';
import { Alert, Loading, useAction, useLoad } from '../../Components/ui';
import type { TabProps } from './ControlPanel';

const MAX_EDIT_BYTES = 2 * 1024 * 1024;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const join = (dir: string, name: string) => (dir === '/' ? '' : dir) + '/' + name;
const parent = (p: string) => p.slice(0, p.lastIndexOf('/')) || '/';

function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * The files API stores text only. Binary files (images, archives, …) are rejected here and go
 * through SFTP or CarbonEdge's web file manager instead.
 */
async function readAsText(file: File): Promise<string> {
    const bytes = new Uint8Array(await file.arrayBuffer());
    if (bytes.includes(0)) throw new Error('binary');
    try {
        return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
    } catch {
        throw new Error('binary');
    }
}

function Editor({ base, path, onClose }: { base: string; path: string; onClose: () => void }) {
    const { data, error, loading } = useLoad(
        () => api.get<{ file: { path: string; content: string } }>(`${base}/files/content`, { params: { path } }).then(r => r.data.file), [base, path]);
    const [content, setContent] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);
    const action = useAction();
    const value = content ?? data?.content ?? '';

    const save = () => action.run(async () => {
        await api.put(`${base}/files/content`, { path, content: value });
        setSaved(true);
    });

    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3 className="mono">{path}</h3>
                <div className="btn-row">
                    <button className="btn-submit inline small" onClick={save} disabled={action.busy || !data}>Save</button>
                    <button className="btn-outline small" onClick={onClose}>Close</button>
                </div>
            </div>
            {error && <Alert type="error">{error}</Alert>}
            {action.error && <Alert type="error">{action.error}</Alert>}
            {saved && <Alert type="success">Saved.</Alert>}
            {loading ? <Loading /> : data && (
                <div className="fi-form">
                    <textarea className="code-editor" spellCheck={false} value={value} onChange={e => { setContent(e.target.value); setSaved(false); }} />
                </div>
            )}
        </div>
    );
}

export default function FilesTab({ base }: TabProps) {
    const [dir, setDir] = useState('/public_html');
    const [editing, setEditing] = useState<string | null>(null);
    const uploadRef = useRef<HTMLInputElement>(null);
    const { data: entries, error, loading, reload } = useLoad(
        () => api.get<{ entries: FileEntry[] }>(`${base}/files`, { params: { path: dir } }).then(r => r.data.entries), [base, dir]);
    const action = useAction();
    // CarbonEdge's browser file manager handles binary files, which this text-only editor cannot.
    const { data: sftp } = useLoad(() => api.get<{ sftp: SftpInfo }>(`${base}/sftp`).then(r => r.data.sftp), [base]);

    const crumbs = dir.split('/').filter(Boolean);

    const newFile = () => {
        const name = window.prompt('File name');
        if (!name) return;
        void action.run(async () => {
            await api.put(`${base}/files/content`, { path: join(dir, name), content: '' });
            await reload();
            setEditing(join(dir, name));
        });
    };
    const newFolder = () => {
        const name = window.prompt('Folder name');
        if (!name) return;
        void action.run(async () => { await api.post(`${base}/files/mkdir`, { path: join(dir, name) }); await reload(); });
    };
    const rename = (entry: FileEntry) => {
        const name = window.prompt('New name', entry.name);
        if (!name || name === entry.name) return;
        void action.run(async () => { await api.post(`${base}/files/rename`, { from: entry.path, to: join(parent(entry.path), name) }); await reload(); });
    };
    const remove = (entry: FileEntry) => {
        if (!window.confirm(`Delete ${entry.path}${entry.type === 'dir' ? ' and everything in it' : ''}?`)) return;
        void action.run(async () => { await api.delete(`${base}/files`, { params: { path: entry.path } }); await reload(); });
    };
    const download = (entry: FileEntry) => action.run(async () => {
        const { file } = (await api.get<{ file: { content: string } }>(`${base}/files/content`, { params: { path: entry.path } })).data;
        const bytes = new TextEncoder().encode(file.content);
        const url = URL.createObjectURL(new Blob([bytes]));
        const a = document.createElement('a');
        a.href = url;
        a.download = entry.name;
        a.click();
        URL.revokeObjectURL(url);
    });
    const upload = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        const tooBig = files.find(f => f.size > MAX_UPLOAD_BYTES);
        if (tooBig) return action.setError(`${tooBig.name} is larger than 10 MB. Use SFTP for large files.`);
        void action.run(async () => {
            for (const file of files) {
                let content: string;
                try {
                    content = await readAsText(file);
                } catch {
                    throw new Error(`${file.name} is a binary file. Upload images and other binary files with SFTP or the web file manager.`);
                }
                await api.put(`${base}/files/content`, { path: join(dir, file.name), content });
            }
            await reload();
        });
    };

    if (editing) return <Editor base={base} path={editing} onClose={() => { setEditing(null); void reload(); }} />;

    return (
        <div className="panel-card">
            <div className="panel-card-header">
                <h3>File manager</h3>
                <div className="btn-row">
                    <button className="btn-outline small" onClick={newFile} disabled={action.busy}>New file</button>
                    <button className="btn-outline small" onClick={newFolder} disabled={action.busy}>New folder</button>
                    <button className="btn-submit inline small" onClick={() => uploadRef.current?.click()} disabled={action.busy}>Upload text files</button>
                    {sftp?.webFileManagerUrl && <a className="btn-outline small" href={sftp.webFileManagerUrl} target="_blank" rel="noopener noreferrer">Web file manager ↗</a>}
                    <input ref={uploadRef} type="file" multiple hidden onChange={upload} />
                </div>
            </div>

            <div className="breadcrumbs">
                <button className="link-button" onClick={() => setDir('/')}>root</button>
                {crumbs.map((c, i) => (
                    <span key={i}>/ <button className="link-button" onClick={() => setDir('/' + crumbs.slice(0, i + 1).join('/'))}>{c}</button></span>
                ))}
            </div>

            {action.error && <Alert type="error">{action.error}</Alert>}
            {error && <Alert type="error">{error}</Alert>}

            {loading && !entries ? <Loading /> : (
                <div className="table-wrap">
                    <table className="data-table">
                        <thead><tr><th>Name</th><th>Size</th><th>Modified</th><th /></tr></thead>
                        <tbody>
                            {dir !== '/' && (
                                <tr className="clickable" onClick={() => setDir(parent(dir))}><td colSpan={4}>📁 ..</td></tr>
                            )}
                            {entries?.map(entry => (
                                <tr key={entry.path}>
                                    <td>
                                        {entry.type === 'dir'
                                            ? <button className="link-button" onClick={() => setDir(entry.path)}>📁 {entry.name}</button>
                                            : <button className="link-button" disabled={(entry.size ?? 0) > MAX_EDIT_BYTES} onClick={() => setEditing(entry.path)}>📄 {entry.name}</button>}
                                    </td>
                                    <td>{entry.type === 'dir' || entry.size === null ? '—' : formatSize(entry.size)}</td>
                                    <td>{formatDate(entry.modifiedAt)}</td>
                                    <td className="actions">
                                        {entry.type === 'file' && <button className="btn-outline small" onClick={() => download(entry)} disabled={action.busy}>Download</button>}
                                        <button className="btn-outline small" onClick={() => rename(entry)} disabled={action.busy}>Rename</button>
                                        <button className="btn-outline small danger" onClick={() => remove(entry)} disabled={action.busy}>Delete</button>
                                    </td>
                                </tr>
                            ))}
                            {entries?.length === 0 && <tr><td colSpan={4} className="empty-state">This folder is empty.</td></tr>}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
